import { gql } from "@apollo/client";

const gqlSchemaLibrary = {
  kits: {
    query: {
      getKitIdForOrganization: gql`
        query GetKitIdForOrganization($kitId: String!) {
          getKitIdForOrganization(kitId: $kitId) {
            _id
            kitId
            kitTypeId
            patientId
            organizationId
            peripherals {
              deviceId
              deviceType
              deviceSubtype
              deviceModel
            }
          }
        }
      `,
    },
    mutation: {
      assignKitToPatient: gql`
        mutation AssignKitToPatient($kitId: String, $patientId: String) {
          assignKitToPatient(kitId: $kitId, patientId: $patientId) {
            _id
            kitId
            kitTypeId
            patientId
            organizationId
            peripherals {
              deviceId
              deviceType
              deviceSubtype
              deviceModel
            }
          }
        }
      `,
    },
  },
  preferences: {
    measurements: {
      MEASUREMENTS_BY_PATIENT: gql`
        query GetPatientMeasurePreferences($patientId: String!) {
          getPatientMeasurePreferences(patientId: $patientId) {
            vital
            readingDay
            readingWeekDay
            readingHourTime
          }
        }
      `,
    },
  },
  notifications: {
    alerts: {
      NOTIFICATIONS_BY_PATIENT: gql`
        query GetAllNotificationsByPatient(
          $patientId: String!
          $programId: String!
        ) {
          getAllNotificationsByPatient(
            patientId: $patientId
            programId: $programId
          ) {
            _id
            patientId
            programId
            createdAt
            validUntil
            acknowledgedAt
            acknowledged
            acknowledgedBy
            trigger
            type
            typeLabel
            valueString
            previousValueString
          }
        }
      `,
      subscriptions: {
        alertNotificationEmitter: gql`
          subscription AlertNotificationEmitter {
            alertNotificationEmitter {
              patientId
              createdAt
              validUntil
              acknowledgedAt
              acknowledged
              acknowledgedBy
              trigger
              type
              typeLabel
              valueString
              previousValueString
            }
          }
        `,
      },
    },
  },
  devices: {
    peripherial: {
      PERIPHERIAL_BY_PATIENT: gql`
        query GetPeripherialsByPatient($patientId: String!) {
          getPeripherialsByPatient(patientId: $patientId) {
            _id
            kitId
            deviceId
            dateUpdated
            batteryVoltage
            deviceSubtype
            deviceType
            dateCreated
            assignedAt
            HubIMEI
            patientId
            displayName
          }
        }
      `,
      PERIPHERIAL_STATUS_BY_PATIENT: gql`
        query GetPeripherialsStatusByPatient($patientId: String!) {
          getPeripherialsStatusByPatient(patientId: $patientId) {
            device {
              kitId
              deviceId
              deviceSubtype
              deviceType
              dateCreated
              assignedAt
              patientId
              displayName
            }
            status {
              dateCreated
              action
              actionBy
              deviceId
              patientId
            }
          }
        }
      `,
      getPeripherialsByOrg: gql`
        query GetPeripherialsByOrg($deviceSubtype: String) {
          getPeripherialsByOrg(deviceSubtype: $deviceSubtype) {
            _id
            kitId
            deviceId
            dateUpdated
            batteryVoltage
            deviceSubtype
            deviceType
            assignedAt
            deviceModel
            dateCreated
            HubIMEI
            patientId
          }
        }
      `,
      query: {
        getPeripherialsSummary: gql`
          query GetPeripherialsSummary {
            getPeripherialsSummary {
              deviceSubtype
              available
              count
              assigned
              displayName
            }
          }
        `,
      },
      mutation: {
        assignPeripheralToPatient: gql`
          mutation AssignPeripheralToPatient(
            $relationship: PeripheralAssignmentInput!
          ) {
            assignPeripheralToPatient(relationship: $relationship)
          }
        `,
        unAssignPeripheralToPatient: gql`
          mutation UnAssignPeripheralToPatient($deviceId: String) {
            unAssignPeripheralToPatient(deviceId: $deviceId)
          }
        `
      },
    },
  },
  data: {
    spo2: {
      LAST_SPO2: gql`
        query GetLastSPO2($patientId: String!) {
          getLastSPO2(patientId: $patientId) {
            createdAt
            data
          }
        }
      `,
      getSPO2SummaryByRange: gql`
        query GetSPO2SummaryByRange(
          $personId: String!
          $start: Float!
          $end: Float
          $confidence: Float
        ) {
          getSPO2SummaryByRange(
            personId: $personId
            start: $start
            end: $end
            confidence: $confidence
          ) {
            max
            min
            mean
            stdDev
            count
            confidenceInterval
          }
        }
      `,
      SPO2_BY_RANGE: gql`
        query GetSpo2ByRange(
          $patientId: String!
          $start: Float
          $end: Float
          $resolution: Float
        ) {
          getSpo2ByRange(
            patientId: $patientId
            start: $start
            end: $end
            resolution: $resolution
          ) {
            createdAt
            data
          }
        }
      `,
      subscription: {
        SPO2_CHANGE: gql`
          subscription onSpo2Changed($patientId: String!) {
            spo2Changed(patientId: $patientId) {
              createdAt
              data
            }
          }
        `,
      },
    },
    temperature: {
      LAST_TEMPERATURE: gql`
        query GetLastTemperature($patientId: String!) {
          getLastTemperature(patientId: $patientId) {
            skin {
              createdAt
              data
            }
            core {
              createdAt
              data
            }
          }
        }
      `,
      getTemperatureSummaryByRange: gql`
        query GetTemperatureSummaryByRange(
          $personId: String!
          $start: Float!
          $end: Float
          $confidence: Float
        ) {
          getTemperatureSummaryByRange(
            personId: $personId
            start: $start
            end: $end
            confidence: $confidence
          ) {
            max
            min
            mean
            stdDev
            count
            confidenceInterval
          }
        }
      `,
      TEMPERATURE_BY_RANGE: gql`
        query GetLastTemperature(
          $patientId: String!
          $start: Float
          $end: Float
          $resolution: Float
        ) {
          getTemperatureByRange(
            patientId: $patientId
            start: $start
            end: $end
            resolution: $resolution
          ) {
            createdAt
            data
          }
        }
      `,
      subscription: {
        TEMPERATURE_CHANGE: gql`
          subscription onTemperatureChanged($patientId: String!) {
            temperatureChanged(patientId: $patientId) {
              createdAt
              data
            }
          }
        `,
      },
    },
    respiratoryRate: {
      RESP_RATE_SUMMARY_BY_RANGE: gql`
        query GetRespRateSummaryByRange(
          $personId: String!
          $start: Float!
          $end: Float
          $confidence: Float
        ) {
          getRespRateSummaryByRange(
            personId: $personId
            start: $start
            end: $end
            confidence: $confidence
          ) {
            max
            min
            mean
            stdDev
            count
            confidenceInterval
          }
        }
      `,
      RESP_RATE_BY_RANGE: gql`
        query GetRespRateByRange(
          $personId: String!
          $start: Float!
          $end: Float
          $resolution: Float
        ) {
          getRespRateByRange(
            personId: $personId
            start: $start
            end: $end
            resolution: $resolution
          ) {
            createdAt
            data
          }
        }
      `,
      LAST_RESP_RATE: gql`
        query GetLastRespRate($personId: String!) {
          getLastRespRate(personId: $personId) {
            createdAt
            data
          }
        }
      `,
      subscription: {
        RR_CHANGE: gql`
          subscription onRespiratoryRateChanged($patientId: String!) {
            respiratoryRateChanged(patientId: $patientId) {
              createdAt
              data
            }
          }
        `,
      },
    },
    heartRate: {
      HEARTRATE_SUMMARY_BY_RANGE: gql`
        query GetHeartRateSummaryByRange(
          $personId: String!
          $start: Float!
          $end: Float
          $confidence: Float
        ) {
          getHeartRateSummaryByRange(
            personId: $personId
            start: $start
            end: $end
            confidence: $confidence
          ) {
            max
            min
            mean
            stdDev
            count
            confidenceInterval
          }
        }
      `,
      HEARTRATE_BY_RANGE: gql`
        query GetHeartRateByRange(
          $personId: String!
          $start: Float!
          $end: Float
          $resolution: Float
        ) {
          getHeartRateByRange(
            personId: $personId
            start: $start
            end: $end
            resolution: $resolution
          ) {
            createdAt
            data
          }
        }
      `,
      LAST_HEARTRATE: gql`
        query GetLastHeartRate($personId: String!) {
          getLastHeartRate(personId: $personId) {
            createdAt
            data
          }
        }
      `,
      subscription: {
        HR_CHANGE: gql`
          subscription onHeartRateChanged($patientId: String!) {
            heartRateChanged(patientId: $patientId) {
              createdAt
              data
            }
          }
        `,
      },
    },
    weight: {
      WEIGHT_SUMMARY: gql`
        query GetWeightSummary($personId: String!) {
          getWeightSummary(personId: $personId) {
            max
            min
            mean
          }
        }
      `,
      WEIGHT_BY_RANGE: gql`
        query GetWeightByRange(
          $personId: String!
          $start: Float!
          $end: Float
        ) {
          getWeightByRange(personId: $personId, start: $start, end: $end) {
            createdAt
            weight
          }
        }
      `,
      LAST_WEIGHT: gql`
        query GetLastWeight($personId: String!) {
          getLastWeight(personId: $personId) {
            createdAt
            tare
            weight
          }
        }
      `,
      subscription: {
        WIGHT_CHANGE: gql`
          subscription onWeightChanged($patientId: String!) {
            weightChanged(patientId: $patientId) {
              createdAt
              weight
              tare
            }
          }
        `,
      },
    },
    bloodPressure: {
      LAST_BP: gql`
        query GetLastBloodPressure($personId: String!) {
          getLastBloodPressure(personId: $personId) {
            createdAt
            diastolic
            systolic
            pulse
            irregular
          }
        }
      `,
      BP_SUMMARY_BY_RANGE: gql`
        query GetBloodPressureSummaryByRange(
          $personId: String!
          $start: Float!
          $end: Float
        ) {
          getBloodPressureSummaryByRange(
            personId: $personId
            start: $start
            end: $end
          ) {
            diastolic {
              max
              min
              mean
            }
            systolic {
              max
              min
              mean
            }
          }
        }
      `,
      BP_BY_RANGE: gql`
        query GetBloodPressureByRange(
          $personId: String!
          $start: Float!
          $end: Float
        ) {
          getBloodPressureByRange(
            personId: $personId
            start: $start
            end: $end
          ) {
            createdAt
            diastolic
            systolic
          }
        }
      `,
      subscription: {
        BP_CHANGE: gql`
          subscription onBloodpressureChanged($patientId: String!) {
            bloodpressureChanged(patientId: $patientId) {
              createdAt
              diastolic
              systolic
              pulse
              irregular
            }
          }
        `,
      },
    },
    activity: {
      LAST_ACTIVITY: gql`
      query GetLastActivityLevel($personId: String!){
          getLastActivityLevel(personId: $personId){
              createdAt
              startTime
              endTime
              attributes {
                  low
                  medium
                  high
                  unknown
              }
          }
      }`
    },
    sleep: {
      LAST_SLEEP: gql`
        query GetLastSleep($personId: String!){
            getLastSleep(personId: $personId){
                createdAt
                startTime
                endTime
                attributes {
                    asleep
                    asleep_not_confirmed
                }
            }
        }`
    },
    ECG: {
      ECG_BY_RANGE: gql`
        query GetECGbyRange($personId: String!, $start: Float!, $end: Float!) {
          getECGbyRange(personId: $personId, start: $start, end: $end) {
            createdAt
            dataPoints
          }
        }
      `,
    },
    GAIT_SPEED: {
      GS_SUBSCRIPTION: gql`
        subscription onGsChanged($patientId: String!) {
          gsChanged(patientId: $patientId) {
            _id
            createdAt
            personId
            value
          }
        }
      `,
      LAST_GS: gql`
        query GetLastGaitSpeed($patientId: String!) {
          getLastGaitSpeed(patientId: $patientId) {
            _id
            createdAt
            personId
            value
          }
        }
      `,
    },
    TIME_TO_RISE: {
      TTR_SUBSCRIPTION: gql`
        subscription onTtrChanged($patientId: String!) {
          ttrChanged(patientId: $patientId) {
            _id
            createdAt
            personId
            value
          }
        }
      `,
      LAST_TTR: gql`
        query GetLastTimeToRise($patientId: String!) {
          getLastTimeToRise(patientId: $patientId) {
            _id
            createdAt
            personId
            value
          }
        }
      `,
    },
    POSITION: {
      POS_SUBSCRIPTION: gql`
        subscription onPositionChanged($patientId: String!) {
          positionChanged(patientId: $patientId) {
            _id
            createdAt
            personId
            value
          }
        }
      `,
      LAST_POSITION: gql`
        query GetLastPosition($patientId: String!) {
          getLastPosition(patientId: $patientId) {
            _id
            createdAt
            personId
            value
          }
        }
      `,
    },
  },
  clinicians: {
    mutations: {
      reactivateUser: gql`
        mutation ReactivateUser($userId: String) {
          reactivateUser(userId: $userId) {
            _id
            email
            isDisabled
            bio {
              lastname
              firstname
              gender
              photo
            }
          }
        }
      `,
      deactivateUser: gql`
        mutation DeactivateUser($userId: String) {
          deactivateUser(userId: $userId) {
            _id
            email
            isDisabled
            bio {
              lastname
              firstname
              gender
              photo
            }
          }
        }
      `,
      createOrgUser: gql`
        mutation CreateOrgUser(
          $user: UserCreateInput!
          $clinician: ClinicianCreateInput!
        ) {
          createOrgUser(user: $user, clinician: $clinician) {
            _id
            email
            bio {
              lastname
              firstname
              gender
              photo
            }
          }
        }
      `,
      editOrgUser: gql`
        mutation EditOrgUser(
          $user: UserEditInput!
          $clinician: ClinicianEditInput!
        ) {
          editOrgUser(user: $user, clinician: $clinician) {
            _id
            email
            bio {
              lastname
              firstname
              gender
              photo
            }
          }
        }
      `,
    },
    queries: {
      getCliniciansByOrg: gql`
        query GetCliniciansByOrg {
          getCliniciansByOrg {
            _id
            userId
            role
            roleGroup
            dateOfBirth
            phones {
              label
              number
              code
            }
            mobile
            user {
              email
              isDisabled
              bio {
                lastname
                firstname
                gender
                photo
              }
            }
          }
        }
      `,
      getClinician: gql`
        query GetClinician($email: String, $_id: String) {
          getClinician(email: $email, _id: $_id) {
            _id
            userId
            role
            clinic
            roleGroup
            dateOfBirth
            organizationIds
            address {
              line
              lineTwo
              type
              references
              zip
              city
              state
              country
            }
            phones {
              label
              number
              code
              receiveSMS
            }
            mobile
            user {
              _id
              email
              isDisabled
              bio {
                lastname
                firstname
                gender
                photo
              }
              receiveEmail
              userRoles {
                alertsBase {
                  read
                  edit
                  create
                }
                alertsPreference {
                  read
                  edit
                  create
                }
                devicesAssign {
                  read
                  edit
                  create
                }
                programsBase {
                  read
                  edit
                  create
                }
                programsEnroll {
                  read
                  edit
                  create
                }
                programsRules {
                  read
                  edit
                  create
                }
                userClinicians {
                  read
                  edit
                  create
                }
                userPatients {
                  read
                  edit
                  create
                }
                userCredentials {
                  read
                  edit
                  create
                }
              }
            }
          }
        }
      `,
    },
  },
  patients: {
    PATIENT_ALERTS: gql`
      query GetPatientAlertConfiguration($patientId: String!) {
        getPatientAlertConfiguration(patientId: $patientId) {
          weight {
            lower
            upper
            modified
          }
          weightChange {
            lower
            upper
            modified
          }
          bloodPressureDiastolic {
            lower
            upper
            modified
          }
          bloodPressureSystolic {
            lower
            upper
            modified
          }
          heartRate {
            lower
            upper
            modified
          }
          respRate {
            lower
            upper
            modified
          }
          spo2 {
            lower
            upper
            modified
          }
          temperature {
            lower
            upper
            modified
          }
        }
      }
    `,
    DETAILED_PATIENT_CARETEAM: gql`
      query getPatientCareTeam($personId: String!) {
        getPatientCareTeam(personId: $personId) {
          role
          roleGroup
          udid
          mobile
          user {
            email
            isDisabled
            bio {
              lastname
              firstname
              gender
              photo
            }
          }
        }
      }
    `,
    getPersonByOrganization: gql`
      query GetPersonByOrganization {
        getPersonByOrganization {
          _id
          dateOfBirth
          lastDataSeen
          programId
          enrolledDate
          isPaused
          pausedAt
          reason
          isDisenrolled
          disenrolledAt
          timeline {
            action
            description
            note
            date
            actor
          }
          expectedUnenrollment
          phones {
            label
            number
            code
          }
          user {
            _id
            isDisabled
            bio {
              lastname
              firstname
              gender
              photo
            }
          }
          requiredDevices
        }
      }
    `,
    getPersonByProgram: gql`
      query GetPersonByProgram($programId: String!) {
        getPersonByProgram(programId: $programId) {
          person {
            _id
            dateOfBirth
            lastDataSeen
            programId
            enrolledDate
            isPaused
            pausedAt
            reason
            isDisenrolled
            disenrolledAt
            expectedUnenrollment
            kitId
            phones {
              label
              number
              code
            }
            user {
              _id
              isDisabled
              bio {
                lastname
                firstname
                gender
                photo
              }
            }
            phones {
              label
              number
              code
            }
          }
          total
        }
      }
    `,
    getPatientsDevicesByType: gql`
      query GetPatientsDevicesByType($patientIds: [String], $deviceType: [String]) {
        getPatientsDevicesByType(patientIds: $patientIds, deviceType: $deviceType) {
          _id
        }
      }
    `,
    countPersonInProgram: gql`
      query CountPersonInProgram {
        countPersonInProgram {
          program
          count
        }
      }
    `,
    getUserPatients: gql`
      query GetUserPatients {
        getUserPatients {
          dateOfBirth
          _id
          lastDataSeen
          user {
            _id
            isDisabled
            bio {
              lastname
              firstname
              gender
              photo
            }
          }
          phones {
            label
            number
            code
          }
        }
      }
    `,
    getUserAndTeamsByPatient: gql`
      query GetUserAndTeamsByPatient($id: String) {
        getUserAndTeamsByPatient(id: $id) {
          clinicians {
            _id
            userId
            role
            roleGroup
            dateOfBirth
            phones {
              label
              number
              code
            }
            mobile
            user {
              email
              isDisabled
              bio {
                lastname
                firstname
                gender
                photo
              }
            }
          }
          teams {
            _id
            name
            description
            users {
              _id
            }
            teams {
              _id
            }
          }
        }
      }
    `,
    queries: {
      getPerson: gql`
        query getPerson($personId: String!) {
          getPerson(_id: $personId) {
            _id
            organizationId
            programId
            enrolledDate
            isPaused
            pausedAt
            reason
            isDisenrolled
            disenrolledAt
            expectedUnenrollment
            timeline {
              action
              description
              note
              date
              actor
            }
            dateOfBirth
            EMPI
            MRN
            admissionReason
            allergies
            address {
              line
              lineTwo
              type
              references
              zip
              city
              state
              country
            }
            contactCareGiver
            phones {
              label
              number
              code
            }
            emergencyContacts {
              contactType
              firstname
              lastname
              relationship
              phones {
                label
                number
                code
              }
              dateOfBirth
              tags
              email
            }
            careTeam
            lastDataSeen
            user {
              _id
              email
              createdAt
              bio {
                lastname
                firstname
                gender
                photo
              }
            }
            bioReport
            requiredDevices
          }
        }
      `,
    },
    subscriptions: {
      lastDataSeenChanged: gql`
        subscription onLastDataSeenChanged($patientIds: [String]!) {
          lastDataSeenChanged(patientIds: $patientIds) {
            createdAt
            personId
          }
        }
      `,
    },
    mutations: {
      editPatient: gql`
        mutation EditPatient(
          $patientToEdit: UserPatientToEdit!
          $userCore: UserCoreInput!
          $patient: UserPatientInput!
        ) {
          editPatient(
            patientToEdit: $patientToEdit
            userCore: $userCore
            patient: $patient
          ) {
            _id
          }
        }
      `,
      createPatient: gql`
        mutation CreatePatient(
          $userCore: UserCoreInput!
          $patient: UserPatientInput!
        ) {
          createPatient(userCore: $userCore, patient: $patient) {
            _id
          }
        }
      `,
      enrollPatient: gql`
        mutation EnrollPatient($enrollments: EnrollPatientInput!) {
          enrollPatient(enrollments: $enrollments)
        }
      `,
      pauseEnrollment: gql`
        mutation PauseEnrollment(
          $personId: String
          $reason: String
          $note: String
        ) {
          pauseEnrollment(personId: $personId, reason: $reason, note: $note) {
            _id
            dateOfBirth
            lastDataSeen
            programId
            enrolledDate
            isPaused
            pausedAt
            reason
            isDisenrolled
            disenrolledAt
            expectedUnenrollment
            kitId
            phones {
              label
              number
              code
            }
            user {
              _id
              bio {
                lastname
                firstname
                gender
                photo
              }
            }
            phones {
              label
              number
              code
            }
            timeline {
              action
              description
              note
              date
              actor
            }
          }
        }
      `,
      unPauseEnrollment: gql`
        mutation UnPauseEnrollment($personId: String) {
          unPauseEnrollment(personId: $personId) {
            _id
            dateOfBirth
            lastDataSeen
            programId
            enrolledDate
            isPaused
            pausedAt
            reason
            isDisenrolled
            disenrolledAt
            expectedUnenrollment
            kitId
            phones {
              label
              number
              code
            }
            user {
              _id
              bio {
                lastname
                firstname
                gender
                photo
              }
            }
            phones {
              label
              number
              code
            }
            timeline {
              action
              description
              note
              date
              actor
            }
          }
        }
      `,
      extendEnrollment: gql`
        mutation ExtendEnrollment(
          $personId: String
          $days: Float
          $reason: String
          $note: String
        ) {
          extendEnrollment(
            personId: $personId
            days: $days
            reason: $reason
            note: $note
          ) {
            _id
            dateOfBirth
            lastDataSeen
            programId
            enrolledDate
            isPaused
            pausedAt
            reason
            isDisenrolled
            disenrolledAt
            expectedUnenrollment
            kitId
            phones {
              label
              number
              code
            }
            user {
              _id
              bio {
                lastname
                firstname
                gender
                photo
              }
            }
            phones {
              label
              number
              code
            }
            timeline {
              action
              description
              note
              date
              actor
            }
          }
        }
      `,
      disenrollPatient: gql`
        mutation DisenrollPatient(
          $personId: String
          $reason: String
          $note: String
        ) {
          disenrollPatient(personId: $personId, reason: $reason, note: $note) {
            _id
            dateOfBirth
            lastDataSeen
            programId
            enrolledDate
            isPaused
            pausedAt
            reason
            isDisenrolled
            disenrolledAt
            expectedUnenrollment
            kitId
            phones {
              label
              number
              code
            }
            user {
              _id
              bio {
                lastname
                firstname
                gender
                photo
              }
            }
            phones {
              label
              number
              code
            }
            timeline {
              action
              description
              note
              date
              actor
            }
          }
        }
      `,
      assignUsersTeams: gql`
        mutation AssignUsersTeams(
          $participant: ParticipantCliniciansTeamsInput!
        ) {
          assignUsersTeams(participant: $participant) {
            _id
          }
        }
      `,
    },
  },
  programs: {
    query: {
      getProgramById: gql`
        query GetProgramById($id: String!) {
          getProgramById(id: $id) {
            _id
            organizationId
            name
            programType
            description
            duration
            supervisorClinician
            legalRequired
            populationParams {
              key
              value
            }
            includeCriteria
            excludeCriteria
            handle
            ecgEnabled
            supervisorClinicianRequired
            callSchedulesRequired
            backupSchedulesEnabled
            backupSchedulesRequired
            onCallSchedules
            onBackupSchedules
            onCallSchedulesInfo
            vitals {
              label
              value
              order
              shortDescription
              descriptionItems {
                key
                value
              }
            }
            trackers {
              label
              value
              order
              shortDescription
              descriptionItems {
                key
                value
              }
            }
            users {
              _id
              userId
              role
              roleGroup
              dateOfBirth
              phones {
                label
                number
                code
              }
              mobile
              user {
                email
                isDisabled
                bio {
                  lastname
                  firstname
                  gender
                  photo
                }
              }
            }
            teams {
              _id
              name
              description
              users {
                _id
              }
              teams {
                _id
              }
              programs {
                _id
                name
              }
            }
          }
        }
      `,
      getProgramReport: gql`
        query GetProgramReport($id: String!) {
          getProgramReport(id: $id) {
            name
            data
          }
        }
      `,
      getProgramBioReports: gql`
        query GetProgramBioReports($hash: String, $programId: String, $participantIds: [String], $startDate: Float, $endDate: Float, $timeZone: String) {
          getProgramBioReports(hash: $hash, programId: $programId, participantIds: $participantIds, startDate: $startDate, endDate: $endDate, timeZone: $timeZone) {
            msg
            code
          }
        }
      `,
      getAlertsReport: gql`
        query GetAlertsReport($id: String!) {
          getAlertsReport(id: $id) {
            name
            data
          }
        }
      `,
      getCurrentOrgPrograms: gql`
        query GetCurrentOrgPrograms($orgId: String) {
          getCurrentOrgPrograms(orgId: $orgId) {
            _id
            organizationId
            name
            duration
            handle
            ecgEnabled
            onCallSchedules
            vitals {
              label
              value
              order
            }
            users {
              _id
            }
            teams {
              _id
            }
          }
        }
      `,
      getUsersByProgram: gql`
        query GetUsersByProgram($id: String!) {
          getUsersByProgram(id: $id) {
            _id
            userId
            role
            roleGroup
            dateOfBirth
            phones {
              label
              number
              code
            }
            mobile
            user {
              email
              isDisabled
              bio {
                lastname
                firstname
                gender
                photo
              }
            }
          }
        }
      `,
      getUsersAndCliniciansByProgram: gql`
        query GetProgramById($id: String!) {
          getProgramById(id: $id) {
            _id
            users {
              _id
              userId
              role
              roleGroup
              dateOfBirth
              phones {
                label
                number
                code
              }
              mobile
              user {
                email
                isDisabled
                bio {
                  lastname
                  firstname
                  gender
                  photo
                }
              }
            }
            teams {
              _id
              name
              description
              users {
                _id
              }
              teams {
                _id
              }
            }
          }
        }
      `,
      getProgramEscalations: gql`
        query GetEscalationRoutineByProgramId($programId: String!) {
          getEscalationRoutineByProgramId(programId: $programId) {
            _id
            programId
            vitalsRules {
              vital
              lte
              gte
              type
            }
            cronString
            timeZone
            actionsToTake {
              action
              perEntry
              whom
              template
              smsMessage
            }
            bioReport
            noData
          }
        }
      `,
    },
    mutation: {
      setOnCallSchedules: gql`
        mutation SetOnCallSchedules($schedule: [String], $programId: String!) {
          setOnCallSchedules(schedule: $schedule, programId: $programId) {
            _id
            onCallSchedules
          }
        }
      `,
      setOnBackupSchedules: gql`
        mutation SetOnBackupSchedules(
          $schedule: [String]
          $programId: String!
        ) {
          setOnBackupSchedules(schedule: $schedule, programId: $programId) {
            _id
            onBackupSchedules
          }
        }
      `,
      declareNewProgram: gql`
        mutation DeclareNewProgram($program: ProgramInput!) {
          declareNewProgram(program: $program) {
            _id
          }
        }
      `,
      editProgram: gql`
        mutation EditProgram($program: ProgramInput!) {
          editProgram(program: $program) {
            _id
          }
        }
      `,
      setSupervisor: gql`
        mutation SetSupervisor($clinicianId: String, $programId: String!) {
          setSupervisor(clinicianId: $clinicianId, programId: $programId) {
            _id
          }
        }
      `,
      editProgramUsers: gql`
        mutation EditProgramUsers($programId: String, $usersIds: [String]) {
          editProgramUsers(programId: $programId, usersIds: $usersIds) {
            _id
          }
        }
      `,
      editProgramTeams: gql`
        mutation EditProgramTeams($programId: String, $teamsIds: [String]) {
          editProgramTeams(programId: $programId, teamsIds: $teamsIds) {
            _id
          }
        }
      `,
      editMembersProgram: gql`
        mutation EditMembersProgram($programMembers: ProgramMembersInput!) {
          editMembersProgram(programMembers: $programMembers) {
            _id
          }
        }
      `,
    },
    subscriptions: {
      reportEmitter: gql`
          subscription ReportEmitter($hash: String!){
            reportEmitter(hash: $hash) {
              hash
              data
            }
          }
        `,
    }
  },
  organizations: {
    query: {
      getCurrentOrg: gql`
        query GetCurrentOrg {
          getCurrentOrg {
            _id
            name
            logoUrl
            domain
            referenceMaterial {
              name
              url
            }
            trainingVideos {
              name
              url
            }
            dateFormat {
              short
              long
            }
            temperatureUnit
            defaultCountry
            weightUnit
            participantForm {
              requiredDevices {
                key
                value
              }
            }
          }
        }
      `,
      getAvailableOrgs: gql`
        query GetAvailableOrgs {
          getAvailableOrgs {
            _id
            name
            logoUrl
            domain
          }
        }
      `,
      getDomainLogo: gql`
        query GetDomainLogo($domain: String) {
          getDomainLogo(domain: $domain)
        }
      `,
    },
    mutation: {
      changeSelectedOrg: gql`
        mutation ChangeSelectedOrg($toOrganizationId: String!) {
          changeSelectedOrg(toOrganizationId: $toOrganizationId) {
            selectedOrgId
          }
        }
      `,
    },
  },
  user: {
    query: {
      getURIFromHash: gql`
           query GetURIFromHash($hash: String){
               getURIFromHash(hash: $hash)
           }`
    }
  },
  auth: {
    mutation: {
      refreshToken: gql`
        mutation RefreshToken($lastActivity: Float!) {
          refreshToken(lastActivity: $lastActivity) {
            token
            expiresIn
          }
        }
      `,
      resetPassword: gql`
        mutation ResetPassword($email: String!) {
          resetPassword(email: $email)
        }
      `,
      validateResetToken: gql`
        mutation ValidateResetToken($resetToken: String!) {
          validateResetToken(resetToken: $resetToken) {
            email
            bio {
              lastname
              firstname
            }
          }
        }
      `,
      newPasswordSet: gql`
        mutation NewPasswordSet($resetToken: String!, $newpassword: String) {
          newPasswordSet(resetToken: $resetToken, newpassword: $newpassword) {
            user {
              email
            }
            token
            isUsed
            dateUsed
          }
        }
      `,
      unlockAccount: gql`
        mutation unlockAccount($unlockAccountToken: String!) {
          unlockAccount(unlockAccountToken: $unlockAccountToken) {
            unlockAccount
          }
        }
      `,
    },
  },
  teams: {
    mutation: {
      createTeam: gql`
        mutation CreateTeam($team: TeamInput!) {
          createTeam(team: $team) {
            _id
          }
        }
      `,
      editMembersTeam: gql`
        mutation EditMembersTeam($teamMembers: TeamMembersInput!) {
          editMembersTeam(teamMembers: $teamMembers) {
            _id
          }
        }
      `,
      editPrograms: gql`
        mutation EditPrograms($teamIds: [String], $programIds: [String]) {
          editPrograms(teamIds: $teamIds, programIds: $programIds) {
            _id
          }
        }
      `,
      editTeam: gql`
        mutation EditTeam($team: TeamEditInput!) {
          editTeam(team: $team) {
            _id
          }
        }
      `,
      deleteTeams: gql`
        mutation DeleteTeams($teamIds: [String]) {
          deleteTeams(teamIds: $teamIds) {
            _id
          }
        }
      `,
    },
    queries: {
      getTeams: gql`
        query GetTeams($orgId: String) {
          getTeams(orgId: $orgId) {
            _id
            name
            users {
              _id
            }
            teams {
              _id
            }
            programs {
              _id
            }
          }
        }
      `,
      getTeam: gql`
        query GetTeam($teamId: String) {
          getTeam(teamId: $teamId) {
            _id
            name
            description
            users {
              _id
              user {
                email
                bio {
                  lastname
                  firstname
                }
              }
            }
            teams {
              _id
              name
            }
            programs {
              _id
              name
            }
          }
        }
      `,
    },
  },
};

export default gqlSchemaLibrary;
